<script setup lang="ts">
import { type SelectionModeEnum } from "@lxc/app-device-common";
import type LxcTable from "@lxc/app-device-common/src/components/LxcTable.vue";
import { storeToRefs } from "pinia";
import type { ComputedRef, FunctionalComponent, Ref, SVGAttributes } from "vue";
import { useAcl } from "vue-simple-acl";
import type { SearchMode } from "~/core/composables/useSearch";
import { DISTINCT_VALUES_KEY } from "~/core/constants/constants";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { QuickFilterGroup } from "~/core/models/QuickFilterGroup.interface";
import type { QuickFilterItem } from "~/core/models/QuickFilterItem.interface";
import {
  type FilterOptions,
  Filters,
  type FilterSelectionValue,
} from "~/core/models/filters";
import { allHaveSame } from "~/core/utils/array";
import filtersUtils from "~/core/utils/filters.utils";
import { versionCompare } from "~/core/utils/versionCompare";
import { CAMPAIGN_PARAMS_SCOPE } from "~/modules/campaign/constants/constants";
import {
  type DeviceTypeFilter,
  useDeviceTypeFilters,
} from "~/modules/dtwin/composables/useDeviceTypeFilters";
import { useFetchDtwinModelUIConfigs } from "~/modules/dtwin/composables/useFetchDtwinModelUIConfigs";
import { useFetchDtwinModels } from "~/modules/dtwin/composables/useFetchDtwinModels";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import {
  DISTINCT_VALUES_LIFECYCLE,
  PATH_DTWINS_ID,
} from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinColumns } from "~/modules/dtwin/models/DtwinColumns.enum";
import { DtwinCompatibilityQuickFilter } from "~/modules/dtwin/models/DtwinCompatibilityQuickFilter.enum";
import type { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import type { DtwinListDisplayContext } from "~/modules/dtwin/models/DtwinListDisplayContext.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinModelUiConfig } from "~/modules/dtwin/models/DtwinModelUiConfig.interface";
import { DtwinSelectionQuickFilter } from "~/modules/dtwin/models/DtwinSelectionQuickFilter.enum";
import type { DynamicUI } from "~/modules/dtwin/models/DynamicUI.interface";
import { getLifeCycleStatesOptions } from "~/modules/dtwin/utils/getLifeCycleStatesOptions";
import { getMergedOrGenericUIConfig } from "~/modules/dtwin/utils/getMergedOrGenericUIConfig";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import type { ColumnNameAndWidthI } from "~/types/table.type";
import {
  calculateTableMinWidth,
  getColumnWidth,
  parseColumnWidth,
} from "~/utils/table.utils";
import ILxcAlertTriangle from "~icons/lxc/alert-triangle";
import ILxcCheckCircle from "~icons/lxc/check-circle";

interface DeviceCompatibilityQuickFilterConfigI {
  filterType: DtwinCompatibilityQuickFilter;
  icon?: FunctionalComponent<SVGAttributes>;
}

const props = defineProps<{
  /**
   * True to reload the list.
   */
  reload?: boolean;

  /**
   * Fleet UID to filter the devices in.
   */
  fleetUid?: string;

  /**
   * Fleet UID to filter the devices out.
   */
  notInFleetUid?: string;

  /**
   * Default filters to apply.
   */
  defaultFilters?: Map<Filters, FilterSelectionValue>;

  /**
   * Search mode
   * @default SearchMode.URL_SEARCH
   */
  searchMode?: SearchMode;

  /**
   * Class for search bar.
   */
  searchBarClass?: string | string[];

  /**
   * Context of the list.
   * @default DtwinListDisplayContext.DTWINS
   */
  context?: DtwinListDisplayContext;

  /**
   * True if the remove action is in progress, false otherwise.
   */
  isRemovingFromFleet?: boolean;

  /**
   * Selected devices.
   */
  selectedDtwins?: Dtwin[];

  /**
   * True to manage the pagination in query parameters, false otherwise.
   */
  useQueryParametersForPagination?: boolean;

  /**
   * Defines the selection mode for multi selection.
   * @value force, noSelection, default
   * @default SelectionModeEnum.DEFAULT
   */
  rowClickSelectionMode?: SelectionModeEnum;

  /**
   * True to disable the redirection to the device page, false otherwise.
   */
  noRedirection?: boolean;

  /**
   * True if the rows are clickable, false otherwise.
   */
  clickable?: boolean;

  /**
   * Columns to display.
   */
  columns?: DtwinColumns[];

  /**
   * True to disable selected devices in order to prevent unselect a selected device, false otherwise.
   */
  disableUnselect?: boolean;

  /**
   * Compatibility criteria RSQL expression.
   * If provided, activate the quick filter on compatible/uncompatible devices.
   */
  compatibilityCriteria?: string;

  /**
   * True to show the quick actions, false otherwise.
   */
  showQuickActions?: boolean;

  /**
   * True to show the search bar, false otherwise.
   */
  showSearchBar?: boolean;

  /**
   * True to show the filters, false otherwise.
   */
  showFilters?: boolean;

  /**
   * True to show the quick filter on device types, false otherwise.
   */
  showQuickFilterOnTypes?: boolean;

  /**
   * True to show the quick filter on selected/unselected devices.
   */
  showQuickFilterOnSelectedDtwins?: boolean;

  /**
   * True if the list is used in the campaign scope, false otherwise.
   * If true, it will only display the devices authorized by the license within the scope of a campaign.
   */
  withinCampaignScope?: boolean;
}>();

const emit = defineEmits([
  "update:reload",
  "update:selected-dtwins",
  "removeFromFleet",
  "rowClick",
]);

const { t, te } = useI18n();

const router = useRouter();

const acl = useAcl();

const initialSelectedDtwins = ref<Dtwin[]>(props.selectedDtwins ?? []);

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  isLoading,
  results,
  error,
  setFilter,
  fetchData,
  search,
  onSearch,
  searchOnSectors,
  filters,
  onSortChange,
} = useFetchDtwins({
  organizationCode: currentOrganization.value?.code,
  searchMode: props.searchMode,
  useQueryParametersForPagination: props.useQueryParametersForPagination,
});

const {
  results: resultsAllDtwinModelUIConfigs,
  fetchAll: fetchAllDtwinModelUIConfigs,
} = useFetchDtwinModelUIConfigs({
  organizationCode: currentOrganization.value?.code,
});

const {
  isLoading: isLoadingAllDtwinModels,
  error: errorAllDtwinModels,
  results: resultsAllDtwinModels,
  fetchAll: fetchAllDtwinModels,
} = useFetchDtwinModels({ organizationCode: currentOrganization.value?.code });

const { selectedSectors } = storeToRefs(useSectorStore());

const deviceModelsFilters: Ref<DeviceTypeFilter[]> = ref([]);

const isAllowedToManageFleet = computed(() =>
  acl.can(ACLRoles.CAN_WRITE_DEVICE),
);

const canDeleteDtwins = computed(() => acl.can(ACLRoles.CAN_WRITE_DEVICE));

const showActionInProgress: ComputedRef<boolean | undefined> = computed(() =>
  props.columns?.includes(DtwinColumns.ACTION_IN_PROGRESS),
);
const actionInProgressLabel: Ref<string> = ref(
  t("dtwins.list.attributes.actionInProgress"),
);

const resultUIConfig: Ref<DynamicUI | undefined> = ref();

const lifecycleStatesOptions: ComputedRef<FilterOptions> = computed(() =>
  getLifeCycleStatesOptions(t, te, results.value?.distinctValues),
);

function applyDefaultFilters() {
  if (props.defaultFilters) {
    for (const [filter, value] of props.defaultFilters) {
      setFilter(filter, value || "");
    }
  }
}

applyDefaultFilters();

const tableRef = ref<InstanceType<typeof LxcTable>>();

const searchQuery = computed({
  get() {
    return (filters.get(Filters.DTWIN_NAME_OR_SERIAL_NUMBER) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.DTWIN_NAME_OR_SERIAL_NUMBER, searchQuery);
  },
});

async function reloadData(page?: number, pageSize?: number) {
  const params = new Map();
  if (
    selectedCompatibilityFilter.value ===
    DtwinCompatibilityQuickFilter.COMPATIBLE_DEVICES
  ) {
    params.set("compatibilityCriteria", props.compatibilityCriteria);
  } else if (
    props.compatibilityCriteria &&
    selectedCompatibilityFilter.value ===
      DtwinCompatibilityQuickFilter.INCOMPATIBLE_DEVICES
  ) {
    params.set(
      "compatibilityCriteria",
      filtersUtils.negateRSQLExpression(props.compatibilityCriteria),
    );
  }
  params.set("showActionInProgress", showActionInProgress.value);

  if (props.showFilters) {
    params.set(DISTINCT_VALUES_KEY, DISTINCT_VALUES_LIFECYCLE);
  }
  params.set(CAMPAIGN_PARAMS_SCOPE, props.withinCampaignScope);

  if (props.showQuickFilterOnSelectedDtwins) {
    const selections = initialSelectedDtwins.value?.map((dtwin) => dtwin.uid);
    if (
      selections.length > 0 &&
      selectedDevicesSelectionFilter.value ===
        DtwinSelectionQuickFilter.SELECTED_DEVICES
    ) {
      setFilter(Filters.DTWIN_UID, selections);
      setFilter(Filters.DTWIN_UID_OUT, []);
    } else if (
      selectedDevicesSelectionFilter.value ===
      DtwinSelectionQuickFilter.UNSELECTED_DEVICES
    ) {
      setFilter(Filters.DTWIN_UID, []);
      setFilter(Filters.DTWIN_UID_OUT, selections);
    } else if (
      selectedDevicesSelectionFilter.value ===
      DtwinSelectionQuickFilter.ALL_DEVICES
    ) {
      setFilter(Filters.DTWIN_UID, []);
      setFilter(Filters.DTWIN_UID_OUT, []);
    }
  }

  await fetchData(page, pageSize, params);

  // If no selected devices, the filter won't be applied
  if (
    props.showQuickFilterOnSelectedDtwins &&
    initialSelectedDtwins.value?.length === 0 &&
    results.value &&
    selectedDevicesSelectionFilter.value ===
      DtwinSelectionQuickFilter.SELECTED_DEVICES
  ) {
    results.value.results = [];
  }

  setSelection();
  initialSelectedDtwins.value = props.selectedDtwins ?? [];
  fetchUIConfigOfLastModel();
}

function fetchUIConfigOfLastModel() {
  let modelUIConfig;

  const selectedModelUids: string[] | undefined = toRaw(
    selectedDeviceModelFilter.value?.value,
  ) as string[] | undefined;

  // get all the selected device models
  const selectedModels = resultsAllDtwinModels.value?.filter((model) =>
    selectedModelUids?.includes(model.uid),
  );

  // get the model UI config of the latest device model only if all the selected device models have the same name
  // otherwise, it corresponds to a filter on multi device types so only all the generic columns must be displayed
  if (
    selectedModelUids &&
    selectedModels &&
    allHaveSame(selectedModels, "name")
  ) {
    // sort the device models by version from the oldest to the latest
    selectedModelUids.sort((modelUid1: string, modelUid2: string) => {
      const model1 = selectedModels?.find((model) => model.uid === modelUid1);
      const model2 = selectedModels?.find((model) => model.uid === modelUid2);

      if (model1 && model2) {
        return versionCompare(model1.version, model2.version);
      } else {
        return 0;
      }
    });

    // get the model UI of the latest device model
    // loop from the latest to the oldest device model until a model UI is found
    for (let i = selectedModelUids.length - 1; i >= 0; i--) {
      modelUIConfig = resultsAllDtwinModelUIConfigs.value?.find((uiConfig) => {
        return uiConfig.deviceModelUid === selectedModelUids[i];
      });

      if (modelUIConfig) {
        break;
      }
    }
  }

  fetchUIConfig(modelUIConfig);
}

/**
 * Select Dtwins
 */
const setSelection = () => {
  if (props.selectedDtwins && tableRef.value && tableRef.value.data) {
    tableRef.value.toggleTableSelectionMode(props.selectedDtwins.length > 0);
    for (const row of tableRef.value.data as Dtwin[]) {
      tableRef.value.toggleRowSelection(
        row,
        props.selectedDtwins?.some(
          (selectedDevice) => selectedDevice.uid === row.uid,
        ),
      );
    }
  }
};

async function executeSearch(replaceHistory?: boolean) {
  if (props.fleetUid) {
    setFilter(Filters.DTWINS_IN_FLEET, props.fleetUid);
  }
  if (props.notInFleetUid) {
    setFilter(Filters.DTWINS_NOT_IN_FLEET, props.notInFleetUid);
  }

  await searchOnSectors(selectedSectors.value, replaceHistory);
}

const selectedDeviceModelFilter = computed(() => {
  return filters.get(Filters.DTWIN_MODEL_TYPE);
});

const quickFiltersWrapperClass = computed(() => {
  const wrapperClass = ["flex", "flex-wrap", "gap-2"];

  if (props.selectedDtwins && props.selectedDtwins.length > 0) {
    wrapperClass.push("mb-2");
  } else if (props.compatibilityCriteria) {
    wrapperClass.push("mb-[1.125rem]", "!gap-4");
  } else {
    wrapperClass.push("mb-6");
  }

  return wrapperClass;
});

async function getDevicesByModel(modelUids?: string[]) {
  if (modelUids) {
    // if unselect device models
    if (
      Array.isArray(selectedDeviceModelFilter?.value?.value) &&
      selectedDeviceModelFilter?.value?.value.length === modelUids.length &&
      (selectedDeviceModelFilter?.value?.value as string[]).every((uid) =>
        modelUids?.includes(uid),
      )
    ) {
      setFilter(Filters.DTWIN_MODEL_TYPE, []);
      fetchUIConfig();
    } else {
      setFilter(Filters.DTWIN_MODEL_TYPE, modelUids);
    }
    await executeSearch();
  }
}

const selectedCompatibilityFilter: Ref<DtwinCompatibilityQuickFilter> = ref(
  DtwinCompatibilityQuickFilter.ALL_DEVICES,
);

async function filterByCompatibility(
  compatibilityFilter: DtwinCompatibilityQuickFilter,
) {
  selectedCompatibilityFilter.value = compatibilityFilter;
  await reloadData();
}

const selectedDevicesSelectionFilter: Ref<DtwinSelectionQuickFilter> = ref(
  DtwinSelectionQuickFilter.ALL_DEVICES,
);

async function filterBySelection(selectionFilter: DtwinSelectionQuickFilter) {
  initialSelectedDtwins.value = props.selectedDtwins ?? [];
  selectedDevicesSelectionFilter.value = selectionFilter;
  await reloadData();
}

const getCompatibilityFilterIconClass = (
  compatibilityFilter: DtwinCompatibilityQuickFilter,
) => {
  if (
    compatibilityFilter !== selectedCompatibilityFilter.value &&
    compatibilityFilter === DtwinCompatibilityQuickFilter.COMPATIBLE_DEVICES
  ) {
    return "text-success-700";
  } else if (
    compatibilityFilter !== selectedCompatibilityFilter.value &&
    compatibilityFilter === DtwinCompatibilityQuickFilter.INCOMPATIBLE_DEVICES
  ) {
    return "text-error-700";
  } else {
    return "text-inherit";
  }
};

const deviceCompatibilityQuickFilters: DeviceCompatibilityQuickFilterConfigI[] =
  [
    {
      filterType: DtwinCompatibilityQuickFilter.ALL_DEVICES,
    },
    {
      filterType: DtwinCompatibilityQuickFilter.COMPATIBLE_DEVICES,
      icon: ILxcCheckCircle,
    },
    {
      filterType: DtwinCompatibilityQuickFilter.INCOMPATIBLE_DEVICES,
      icon: ILxcAlertTriangle,
    },
  ];

const quickFilterGroups = computed(() => {
  const groups: QuickFilterGroup[] = [];

  // Group 1: Device Models
  if (deviceModelsFilters.value.length > 0 && props.showQuickFilterOnTypes) {
    groups.push({
      key: "deviceModels",
      items: deviceModelsFilters.value.map((model) => ({
        key: model.value,
        label: model.label,
        uids: model.uids ? [...model.uids] : [],
        isActive: (
          filters.get(Filters.DTWIN_MODEL_TYPE)?.value as string[]
        )?.some((uid) => model.uids?.includes(uid)),
      })),
      handler: (item: QuickFilterItem) => getDevicesByModel(item.uids),
    });
  }

  // Group 2: Compatibility
  if (props.compatibilityCriteria) {
    groups.push({
      key: "compatibility",
      items: deviceCompatibilityQuickFilters.map((filterConfig) => ({
        key: filterConfig.filterType,
        label: t(
          `dtwins.filters.compatibilityQuickFilters.${filterConfig.filterType}`,
        ),
        icon: filterConfig.icon,
        isActive: selectedCompatibilityFilter.value === filterConfig.filterType,
        iconClass: getCompatibilityFilterIconClass(filterConfig.filterType),
      })),
      handler: (item) =>
        filterByCompatibility(item.key as DtwinCompatibilityQuickFilter),
    });
  }

  // Group 3: Selection
  if (props.showQuickFilterOnSelectedDtwins) {
    const selectionFilters = [
      DtwinSelectionQuickFilter.ALL_DEVICES,
      DtwinSelectionQuickFilter.SELECTED_DEVICES,
      DtwinSelectionQuickFilter.UNSELECTED_DEVICES,
    ];
    groups.push({
      key: "selection",
      items: selectionFilters.map((filter) => ({
        key: filter,
        label: t(`dtwins.filters.selectionQuickFilters.${filter}`),
        isActive: selectedDevicesSelectionFilter.value === filter,
      })),
      handler: (item) =>
        filterBySelection(item.key as DtwinSelectionQuickFilter),
    });
  }

  return groups;
});

function handleFilterClick({
  group,
  item,
}: {
  group: QuickFilterGroup;
  item: QuickFilterItem;
}) {
  // Reset the filter on device range
  setFilter(Filters.DTWIN_RANGE, []);

  // The 'handler' function is still defined in the computed property
  // This keeps the logic for what to do with the click in the parent.
  if (group.handler) {
    group.handler(item);
  }
}

const handleSelect = (selectedDevices: Dtwin[]) => {
  emit(
    "update:selected-dtwins",
    props.selectedDtwins
      ?.filter(
        (selectedDevice: Dtwin) =>
          !tableRef.value ||
          !(tableRef.value.data as Dtwin[])?.find(
            (row: Dtwin) => row.uid === selectedDevice.uid,
          ),
      )
      .filter(
        (selectedDevice: Dtwin) =>
          !selectedDevices.find((row) => row.uid === selectedDevice.uid),
      )
      .concat(selectedDevices) ?? selectedDevices,
  );
};

const onCancelSelection = () => {
  emit("update:selected-dtwins", []);
  if (tableRef.value) {
    tableRef.value.selectedRows = [];
  }
};

const onRowClick = (dtwin: Dtwin) => {
  emit("rowClick", dtwin);
  if (!props.noRedirection) {
    router.push({ name: PATH_DTWINS_ID, params: { id: dtwin.uid } });
  }
};

/**
 * Build a columns width list from the UI config columns min width or width
 * Example:
 * [{
 *   columnName: "attributes.friendlyName",
 *   width: 10
 * }, ...
 * ]
 */
const columnsWidth: ComputedRef<ColumnNameAndWidthI<DtwinColumns>[]> = computed(
  () => {
    const columnsNameAndWidth: ColumnNameAndWidthI<DtwinColumns>[] = [];

    if (resultUIConfig.value?.list?.columns) {
      for (let i = 0; i < resultUIConfig.value.list.columns.length; i++) {
        const dynamicUIColumn = resultUIConfig.value.list.columns[i];
        const widthRem = dynamicUIColumn.minWidth || dynamicUIColumn.width;
        if (widthRem !== undefined) {
          const width = parseColumnWidth(widthRem);
          if (width !== undefined) {
            columnsNameAndWidth.push({
              columnName: dynamicUIColumn.id as DtwinColumns,
              width,
            });
          }
        }
      }
    }

    return columnsNameAndWidth.concat([
      {
        columnName: DtwinColumns.ACTION,
        width: 6,
      },
      {
        columnName: DtwinColumns.ACTION_IN_PROGRESS,
        width: 12,
      },
    ]);
  },
);

const tableMinWidth: ComputedRef<string | undefined> = computed(() => {
  return calculateTableMinWidth<DtwinColumns>(
    columnsWidth.value,
    props.columns,
  );
});

const getWidth = (columnName: DtwinColumns): string | undefined => {
  return getColumnWidth<DtwinColumns>(columnName, columnsWidth.value);
};

function isRowDisabled(row: Dtwin): boolean {
  return (
    props.disableUnselect &&
    !!initialSelectedDtwins.value?.find((value: Dtwin) => {
      return value.uid === row.uid;
    })
  );
}

function onRemoveFromFleet(dtwins: Dtwin[]) {
  emit("removeFromFleet", dtwins);
}

const onDeleteDtwins = () => {
  reloadData();
  onCancelSelection();
};

const typeOptions = computed(() => {
  if (deviceModelsFilters.value.length > 0) {
    return {
      label: t("dtwins.filters.deviceType"),
      options: deviceModelsFilters.value.map((model) => ({
        value: model.uids || [],
        label: model.label,
        name: model.value,
        disabled: !!props.withinCampaignScope,
      })),
    };
  }
  return undefined;
});

function fetchUIConfig(modelUIConfig?: DtwinModelUiConfig) {
  resultUIConfig.value = getMergedOrGenericUIConfig(modelUIConfig?.properties);
}

function onLifeCycleStateUpdated(
  dtwinUid: string,
  updatedStates: {
    lifeCycleState: DtwinLifeCycleState;
    previousLifeCycleState: DtwinLifeCycleState;
  },
) {
  // Local update in the list
  const dtwinInList = results.value?.results?.find(
    (dtwin) => dtwin.uid === dtwinUid,
  );

  if (dtwinInList) {
    dtwinInList.previousLifeCycleState = updatedStates.previousLifeCycleState;
    dtwinInList.lifeCycleState = updatedStates.lifeCycleState;
  }
}

function findDtwinModel(dtwinModelUid: string): DtwinModel | undefined {
  return resultsAllDtwinModels.value?.find(
    (dtwinModel: DtwinModel) => dtwinModel.uid === dtwinModelUid,
  );
}

watch(() => props.selectedDtwins, setSelection);

/**
 * Reload data if global sector filter has been applied
 */
watch(selectedSectors, () => executeSearch(true));

watch(
  () => props.fleetUid,
  () => executeSearch(),
);

watch(
  () => props.defaultFilters,
  () => {
    applyDefaultFilters();
    reloadData();
  },
);

watch(
  () => props.reload,
  (reload) => {
    if (reload) {
      reloadData();
      emit("update:reload", false);
    }
  },
);

onMounted(async () => {
  await fetchAllDtwinModels();
  await fetchAllDtwinModelUIConfigs();
  fetchUIConfig();
  const { deviceTypeFilters } = await useDeviceTypeFilters(
    currentOrganization.value?.code,
  );
  deviceModelsFilters.value = deviceTypeFilters.value;
  await onSearch(reloadData, false);
  await executeSearch(true);
});

defineExpose({
  onCancelSelection,
});
</script>

<template>
  <div v-if="showSearchBar" :class="`mb-6 ${searchBarClass}`">
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('dtwins.filters.searchByNameOrSerialNumber')"
      @clear="search"
      @search="search"
    />
  </div>

  <container-component
    :is-loading="
      (!resultsAllDtwinModels && !errorAllDtwinModels) || (!results && !error)
    "
    :px="0"
    :py="0"
  >
    <div class="flex justify-between">
      <!-- Avoid ugly display of the quick filters before the list has been displayed. The quick filter arrives with the list the first time the list is loaded -->
      <div
        v-if="
          quickFilterGroups.length > 0 &&
          (resultsAllDtwinModels || errorAllDtwinModels) &&
          (results || error)
        "
      >
        <list-quick-filters
          :groups="quickFilterGroups"
          :class="quickFiltersWrapperClass"
          @filter-click="handleFilterClick"
        />
      </div>
      <dtwin-filters
        v-if="showFilters"
        :organization-code="currentOrganization?.code"
        :filters="filters"
        :type-options="typeOptions"
        :lifecycle-states-options="lifecycleStatesOptions"
        :disable-section-device-type="withinCampaignScope"
        :disable-section-lifecycle-state="withinCampaignScope"
        @change="setFilter"
        @enter="search"
      />
    </div>

    <dtwin-list-quick-actions
      v-if="selectedDtwins && selectedDtwins.length > 0 && showQuickActions"
      :context="context"
      :not-in-fleet-uid="notInFleetUid"
      :selected-dtwins="selectedDtwins"
      :can-delete-dtwins="canDeleteDtwins"
      :organization-code="currentOrganization?.code"
      @cancel-selection="onCancelSelection"
      @remove-from-fleet="onRemoveFromFleet"
      @delete-dtwins="onDeleteDtwins"
    />
    <lxc-table
      ref="tableRef"
      :min-width="tableMinWidth"
      :context="results?.pagination"
      :data="results?.results"
      :is-loading="isLoadingAllDtwinModels || isLoading"
      :error="errorAllDtwinModels?.toError() || error?.toError()"
      :empty-text="t('dtwins.empty')"
      :clickable="clickable"
      :row-click-selection-mode="rowClickSelectionMode"
      :is-row-disabled="isRowDisabled"
      @change-page-and-page-size="reloadData"
      @row-click="onRowClick"
      @select="handleSelect"
      @sort-change="onSortChange"
    >
      <lxc-table-column v-if="selectedDtwins" type="selection" />
      <lxc-table-column
        v-for="column in resultUIConfig?.list?.columns"
        :key="column.id"
        :prop="column.id"
        :label="column.label"
        :sort-mode="column.sortMode"
        :width="column.width"
        :min-width="column.minWidth"
        :class="column.noVerticalPadding ? '!py-0' : ''"
      >
        <template v-if="column.formattingProcess" #default="scope">
          <dtwin-formatting-process
            :dynamic-ui="column"
            :dtwin="scope.row"
            :dtwin-model="findDtwinModel(scope.row.deviceModelUid)"
            :context="context"
            @update="(data) => onLifeCycleStateUpdated(scope.row.uid, data)"
          />
        </template>
      </lxc-table-column>
      <lxc-table-column
        v-if="columns?.includes(DtwinColumns.ACTION_IN_PROGRESS)"
        :label="actionInProgressLabel"
        :width="getWidth(DtwinColumns.ACTION_IN_PROGRESS)"
      >
        <template #default="scope">
          <div
            v-if="scope.row.actionInProgress"
            class="rounded-full bg-primary-100 size-6 flex items-center justify-center"
            :title="actionInProgressLabel"
          >
            <i-lxc-check class="text-primary-600" width="12" height="12" />
          </div>
        </template>
      </lxc-table-column>
      <lxc-table-column
        v-if="columns && columns.includes(DtwinColumns.ACTION)"
        class="!py-0"
        :width="getWidth(DtwinColumns.ACTION)"
      >
        <template #default="scope">
          <div class="flex gap-4">
            <dtwin-actions
              :dtwin="scope.row"
              :dtwin-model="findDtwinModel(scope.row.deviceModelUid)"
              :context="context"
              :is-allowed-to-manage-fleet="isAllowedToManageFleet"
              :is-removing-from-fleet="isRemovingFromFleet"
              @remove-from-fleet="$emit('removeFromFleet', [scope.row])"
              @life-cycle-state-updated="reloadData"
            />
          </div>
        </template>
      </lxc-table-column>
    </lxc-table>
  </container-component>
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;

          svg {
            height: 20px;
            width: 20px;
          }
        }
      }
    }
  }
}
</style>
